import { useEffect } from 'react';

import { BrowserRouter } from 'react-router-dom';
import { ToastContainer, Zoom } from 'react-toastify';

import { createTheme, ThemeProvider as ThemeProviderMUI } from '@mui/material/styles';

import { InitializationProvider } from 'components/InitializationProvider';
import ThemeFallback from 'components/ThemeFallback';
import ViewportHeightProvider from 'components/ViewportHeightProvider';
import ProviderContext from 'contexts';
import { MeetingProvider } from 'global/MeetingContext';
import {
  ThemeProvider as CustomThemeProvider,
  useThemeContext,
} from 'global/ThemeContext/ThemeContext';
import { usePageTitle } from 'hooks/usePageTitle';
import DynamicStyledThemeProvider from 'providers/DynamicStyledThemeProvider';
import 'react-toastify/dist/ReactToastify.css';
import AuthRoutes from 'routes/AuthRoutes';
import GlobalStyles from 'styles/global';
import AntProvider from 'styles/stylesProviderAnt';

const ThemedApp = () => {
  const theme = useThemeContext();

  // Garante que temos valores válidos para o tema
  const safeTheme = {
    primary: theme?.primary || '#DBB958',
    text: {
      primary: theme?.text?.primary || '#E9E9F0',
      secondary: theme?.text?.secondary || '#CECECE',
    },
    background: {
      primary: theme?.background?.primary || '#0A0A0A',
      secondary: theme?.background?.secondary || '#161717',
    },
    error: theme?.error || '#FF0000',
    success: theme?.success || '#00E23F',
    warning: theme?.warning || '#FFA500',
    info: theme?.info || '#0000FF',
  };

  const muiTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: { main: safeTheme.primary },
      secondary: { main: safeTheme.text.primary },
      text: {
        primary: safeTheme.text.primary,
        secondary: safeTheme.text.secondary,
      },
      background: {
        default: safeTheme.background.primary,
        paper: safeTheme.background.secondary,
      },
      error: { main: safeTheme.error },
      success: { main: safeTheme.success },
      warning: { main: safeTheme.warning },
      info: { main: safeTheme.info },
    },
    typography: {
      allVariants: {
        color: safeTheme.text.primary,
      },
    },
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: safeTheme.text.primary,
          },
          root: {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: safeTheme.text.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: safeTheme.text.primary,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: safeTheme.text.primary,
            '&.Mui-focused': {
              color: safeTheme.text.primary,
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: safeTheme.text.primary,
            '&.Mui-selected': {
              color: safeTheme.primary,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: safeTheme.primary,
            '&.Mui-checked': {
              color: safeTheme.primary,
            },
            '&.Mui-disabled': {
              color: '#888', // fallback para disabled
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: safeTheme.primary,
            '&.Mui-checked': {
              color: safeTheme.primary,
            },
            '&.Mui-disabled': {
              color: '#888', // fallback para disabled
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            padding: '0.5rem',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            padding: '0.5rem',
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            color: safeTheme.text.primary,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: safeTheme.text.primary,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          },
        },
      },
    },
  });

  return (
    <ThemeProviderMUI theme={muiTheme}>
      <AntProvider>
        <DynamicStyledThemeProvider>
          <ViewportHeightProvider>
            <GlobalStyles />
            <InitializationProvider>
              <MeetingProvider>
                <AuthRoutes />
              </MeetingProvider>
            </InitializationProvider>
          </ViewportHeightProvider>
        </DynamicStyledThemeProvider>
      </AntProvider>
    </ThemeProviderMUI>
  );
};

const App = () => {
  usePageTitle();

  useEffect(() => {
    // Corrige erro do ResizeObserver
    const resizeObserverLoopErr = /ResizeObserver loop limit exceeded/;
    const originalConsoleError = console.error;

    console.error = (...args) => {
      if (args[0] && resizeObserverLoopErr.test(args[0])) {
        // Ignore the error
        return;
      }
      originalConsoleError(...args);
    };

    // Monitoramento de tema SEM reload automático
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Quando a página volta a ficar visível, apenas monitora o tema
        const bodyBackground = getComputedStyle(document.body).backgroundColor;
        const expectedBackground = 'rgb(10, 10, 10)'; // #0A0A0A

        if (bodyBackground !== expectedBackground && bodyBackground !== 'rgba(0, 0, 0, 0)') {
          console.warn(
            '[App] Problema de tema detectado - usuário pode recarregar manualmente se necessário',
            {
              currentBackground: bodyBackground,
              expectedBackground,
              timestamp: new Date().toISOString(),
            }
          );
          // SEM reload automático - apenas log para debugging
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <BrowserRouter>
      <ProviderContext>
        <CustomThemeProvider>
          <ThemeFallback>
            <ThemedApp />
            <ToastContainer
              theme="dark"
              transition={Zoom}
              autoClose={5000}
              pauseOnHover={true}
              limit={3}
              draggablePercent={60}
            />
          </ThemeFallback>
        </CustomThemeProvider>
      </ProviderContext>
    </BrowserRouter>
  );
};

export default App;
